import { useTranslation } from "react-i18next";
import { Skeleton } from "@/components/ui/skeleton";
import { usePreciseTime } from "@/hooks/use-precise-time";
import { NAMESPACES } from "@/i18n/constants";
import { cn } from "@/utils/class-name";
import { SensorReadingRange, useZoneSensorReadings } from "./use-zone-sensor-readings";

/**
 * Displays a sensor reading range
 */
function SensorReadingRow({ reading }: { reading: SensorReadingRange }) {
    // usePreciseTime requires a timestamp; else the fallback result is unused
    const { timeStringWithAgoSuffix } = usePreciseTime((reading.date ? new Date(reading.date) : new Date()).getTime());
    const { t } = useTranslation(NAMESPACES.COMMON);

    const dataTypeLabel = reading.labelTranslationKey
        ? t(`data-type-labels.${reading.labelTranslationKey}`)
        : reading.dataType;

    // Determine how to display the data, either as a single sensor reading or a range
    const label = reading.shouldDisplayAsSingleSensorReading
        ? dataTypeLabel
        : `${t("measurement.range-label", { label: dataTypeLabel })}:`;
    const units = reading.units ? ` ${reading.units}` : "";
    const value = reading.shouldDisplayAsSingleSensorReading
        ? reading.minValue
        : `${t("measurement.value-range-label", { value1: reading.minValue, value2: reading.maxValue })}`;

    return (
        <div
            className={cn(
                "flex flex-row gap-2 w-full justify-between text-sm",
                !reading.shouldDisplayAsSingleSensorReading ? "items-end xs:items-center" : "items-center",
            )}
        >
            <div
                className={cn(
                    "flex gap-1",
                    !reading.shouldDisplayAsSingleSensorReading ? "flex-col xs:flex-row" : "flex-row",
                )}
            >
                <p>{label}</p>
                <p>
                    {value}
                    {units}
                </p>
            </div>
            <p className={"text-muted-foreground/80"}>{reading.date ? timeStringWithAgoSuffix : t("status.unknown")}</p>
        </div>
    );
}

interface ZoneSensorReadingsProps {
    zoneId: string;
}

export function ZoneSensorReadings({ zoneId }: ZoneSensorReadingsProps) {
    const { sensorReadingsByDataType, isLoading } = useZoneSensorReadings(zoneId);
    const entries = Object.entries(sensorReadingsByDataType ?? {});

    if (isLoading) {
        return (
            <div className={"flex flex-col border border-border rounded-md divide-y divide-border"}>
                <Skeleton className={"h-20 rounded-lg"} />
            </div>
        );
    }

    if (entries.length === 0) return null;

    return (
        <div className={"flex flex-col border border-border rounded-md divide-y divide-border"}>
            {entries.map(([dataType, reading]) => (
                <div key={dataType} className={"p-2"}>
                    <SensorReadingRow reading={reading} />
                </div>
            ))}
        </div>
    );
}
